// ============================================================
// HOUSE OF CARDS — MainMenu v1 — FRONT OF HOUSE
// Terror-standard menu: manor silhouette, one lit window,
// rising embers, save-aware buttons, hardcore surfaced.
//   New Game → confirms over an existing save (the curse rule)
//   Continue → only when a save exists; shows where you left off
//   Settings → ambient/motion/audio toggles (audio bed = Phase 2)
// All tokens from theme.js. Never hardcode color/font here.
// ============================================================
import React, { useState } from "react";
import { RM, emberGlow, veinGlow, ITEM_RARITY, RARITY_COLORS } from "./theme";
import { ITEMS } from "./roomData";
import { ITEM_ICONS } from "./HotspotArt";

const ROOM_LABEL = {
  hall: "the Hall",
  nursery: "the Nursery",
  study: "the Study",
  kitchen: "the Kitchen",
};

export default function MainMenu({
  hasSave,            // bool — a real save exists
  save,               // the loaded state (for the Continue subtitle / hardcore badge)
  onNewGame,          // () => start prologue (caller wipes if needed)
  onSkipPrologue,     // () => jump to the deal
  onCursedMode,       // () => start game in Cursed Mode (test only)
  onContinue,         // () => resume manor
  settings,           // { motion, ambient, muted }
  onSettings,         // (patch) => merge settings
  onDevUnlockEyes,    // (on:boolean) => force the unstableEyes flag (test only)
  onDevStarPower,     // (on:boolean) => force Star Power on (test only)
  devStarPower,       // bool — current test Star Power state
}) {
  const [confirmNew, setConfirmNew] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPockets, setShowPockets] = useState(false);

  const inventory = save?.inventory || [];

  const cursed = !!save?.hardcore;
  const where = save ? (ROOM_LABEL[save.currentRoom] || "the manor") : null;
  const deaths = save?.deaths || 0;

  return (
    <div style={S.wrap}>
      {/* ── atmosphere ── */}
      <ManorSilhouette />
      <Embers />
      <div style={S.vignette} className="menu-vignette" />

      {/* ── title plate ── */}
      <div style={S.titleBlock}>
        <div style={S.kicker}>an ILLEGAL EMPIRE digital novel</div>
        <div style={S.title} className="menu-breathe">HOUSE of CARDS</div>
        <div style={S.sub}>the ground was burned for a reason</div>
      </div>

      {/* ── buttons ── */}
      {!confirmNew ? (
        <div style={S.menu}>
          {hasSave && (
            <button style={S.primary} onClick={onContinue} className="menu-btn">
              <span style={S.btnLabel}>Continue</span>
              <span style={S.btnSub}>
                {cursed ? "☠ " : ""}back in {where}
                {deaths > 0 ? ` · ${deaths} death${deaths > 1 ? "s" : ""}` : ""}
              </span>
            </button>
          )}

          <button
            style={hasSave ? S.ghost : S.primary}
            className="menu-btn"
            onClick={() => (hasSave ? setConfirmNew(true) : onNewGame())}
          >
            <span style={S.btnLabel}>New Game</span>
            <span style={S.btnSub}>the fire, then the rebuild</span>
          </button>

          <button style={S.ghost} className="menu-btn" onClick={onSkipPrologue}>
            <span style={S.btnLabel}>Skip Prologue</span>
            <span style={S.btnSub}>straight to the deal at the porch</span>
          </button>

          <button style={S.ghost} className="menu-btn" onClick={onCursedMode}>
            <span style={S.btnLabel}>Cursed Mode (Test)</span>
            <span style={S.btnSub}>jump straight in under the curse</span>
          </button>

          {hasSave && (
            <button style={S.ghost} className="menu-btn" onClick={() => setShowPockets(true)}>
              <span style={S.btnLabel}>Pockets</span>
              <span style={S.btnSub}>
                {inventory.length
                  ? `what you're carrying · ${inventory.length} item${inventory.length > 1 ? "s" : ""}`
                  : "empty for now"}
              </span>
            </button>
          )}

          <button
            style={S.ghostSlim}
            className="menu-btn"
            onClick={() => setShowSettings((v) => !v)}
          >
            <span style={S.btnLabel}>Settings</span>
          </button>
        </div>
      ) : (
        // ── New Game confirm (the curse keeps what it kills) ──
        <div style={S.confirm}>
          <div style={S.confirmTitle}>Burn it down and start over?</div>
          <div style={S.confirmBody}>
            {cursed
              ? "This save is HARDCORE. Starting a new game ends it for good."
              : "A new game overwrites your current progress in the manor."}
          </div>
          <div style={S.confirmRow}>
            <button style={S.danger} className="menu-btn" onClick={onNewGame}>
              Light the match
            </button>
            <button style={S.ghostSlim} className="menu-btn" onClick={() => setConfirmNew(false)}>
              Keep my save
            </button>
          </div>
        </div>
      )}

      {/* ── pockets drawer ── */}
      {showPockets && (
        <div style={S.settingsVeil} onClick={() => setShowPockets(false)}>
          <div style={S.settingsCard} onClick={(e) => e.stopPropagation()}>
            <div style={S.settingsTitle}>Pockets</div>
            {inventory.length === 0 ? (
              <div style={S.pocketsEmpty}>
                Empty. The house notices empty hands.
              </div>
            ) : (
              <div style={S.pocketsGrid}>
                {inventory.map((id) => {
                  const it = ITEMS[id];
                  const rarity = RARITY_COLORS[ITEM_RARITY[id]] || RM.boneDim;
                  const Icon = ITEM_ICONS[id];
                  return (
                    <div
                      key={id}
                      className="plaque"
                      style={{ ...S.pocketItem, borderColor: rarity }}
                      title={it?.desc}
                    >
                      <div style={{ ...S.pocketSlot, color: rarity, borderColor: `${rarity}55` }}>
                        {Icon ? <Icon /> : (it?.name || "?").slice(0, 2)}
                      </div>
                      <div>
                        <div style={S.pocketName}>{it?.name || id}</div>
                        {it?.desc && <div style={S.pocketDesc}>{it.desc}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <button style={S.closeBtn} className="menu-btn" onClick={() => setShowPockets(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── settings drawer ── */}
      {showSettings && (
        <div style={S.settingsVeil} onClick={() => setShowSettings(false)}>
          <div style={S.settingsCard} onClick={(e) => e.stopPropagation()}>
            <div style={S.settingsTitle}>Settings</div>
            <Toggle
              label="Ambient effects"
              hint="drifting veins, breathing vignette"
              on={settings.ambient}
              onToggle={() => onSettings({ ambient: !settings.ambient })}
            />
            <Toggle
              label="Reduced motion"
              hint="calms embers, sway, and flashes"
              on={!settings.motion}
              onToggle={() => onSettings({ motion: !settings.motion })}
            />
            <Toggle
              label="Mute"
              hint="silence all music and stings"
              on={settings.muted}
              onToggle={() => onSettings({ muted: !settings.muted })}
            />
            {onDevUnlockEyes && (
              <Toggle
                label="Unstable Eyes (test)"
                hint="force-unlock the eyes toggle to test the look"
                on={!!save?.flags?.unstableEyes}
                onToggle={() => onDevUnlockEyes(!save?.flags?.unstableEyes)}
              />
            )}
            {onDevStarPower && (
              <Toggle
                label="Star Power (test)"
                hint="force Star Power on — plays the finale theme + unlocks the mushroom ring"
                on={!!devStarPower}
                onToggle={() => onDevStarPower(!devStarPower)}
              />
            )}
            <button style={S.closeBtn} className="menu-btn" onClick={() => setShowSettings(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      <div style={S.footer}>house-of-cards · v0.2 · mobile m2</div>
    </div>
  );
}

// ── small toggle row ───────────────────────────────────────────
function Toggle({ label, hint, on, onToggle }) {
  return (
    <button style={S.toggleRow} className="menu-btn" onClick={onToggle}>
      <div>
        <div style={S.toggleLabel}>{label}</div>
        {hint && <div style={S.toggleHint}>{hint}</div>}
      </div>
      <div style={{ ...S.switch, ...(on ? S.switchOn : {}) }}>
        <div style={{ ...S.knob, ...(on ? S.knobOn : {}) }} />
      </div>
    </button>
  );
}

// ── manor silhouette: one lit window in a dead house ───────────
function ManorSilhouette() {
  return (
    <svg style={S.silhouette} viewBox="0 0 800 300" preserveAspectRatio="xMidYMax slice" aria-hidden>
      <defs>
        <radialGradient id="hocWin" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={RM.emberBright} stopOpacity="0.95" />
          <stop offset="55%" stopColor={RM.ember} stopOpacity="0.5" />
          <stop offset="100%" stopColor={RM.ember} stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* roofline + body, charred black on the page bg */}
      <path
        d="M0,300 L0,150 L120,150 L120,90 L210,30 L300,90 L300,150
           L360,150 L360,60 L470,60 L470,150 L520,150 L520,110 L640,110
           L640,150 L800,150 L800,300 Z"
        fill={RM.char0}
        stroke={RM.charBorder}
        strokeWidth="2"
      />
      {/* dark windows */}
      <rect x="150" y="170" width="34" height="46" fill={RM.char1} stroke={RM.charBorder} />
      <rect x="246" y="170" width="34" height="46" fill={RM.char1} stroke={RM.charBorder} />
      <rect x="556" y="170" width="34" height="46" fill={RM.char1} stroke={RM.charBorder} />
      <rect x="690" y="170" width="34" height="46" fill={RM.char1} stroke={RM.charBorder} />
      {/* the one lit window */}
      <circle cx="412" cy="110" r="70" fill="url(#hocWin)" className="menu-window-glow" />
      <rect x="396" y="86" width="32" height="48" fill={RM.emberDim} stroke={RM.ember} strokeWidth="1.5" />
      <line x1="412" y1="86" x2="412" y2="134" stroke={RM.ember} strokeWidth="1" opacity="0.7" />
      <line x1="396" y1="110" x2="428" y2="110" stroke={RM.ember} strokeWidth="1" opacity="0.7" />
    </svg>
  );
}

// ── rising ember particles ─────────────────────────────────────
function Embers() {
  const seeds = React.useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 8,
        dur: 7 + Math.random() * 8,
        size: 2 + Math.random() * 3,
        drift: (Math.random() - 0.5) * 60,
      })),
    []
  );
  return (
    <div style={S.emberLayer} aria-hidden>
      {seeds.map((e) => (
        <span
          key={e.id}
          className="menu-ember"
          style={{
            left: `${e.left}%`,
            width: e.size,
            height: e.size,
            animationDelay: `${e.delay}s`,
            animationDuration: `${e.dur}s`,
            "--drift": `${e.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

const S = {
  wrap: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  silhouette: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "42%",
    pointerEvents: "none",
    opacity: 0.9,
  },
  emberLayer: { position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" },
  vignette: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "radial-gradient(ellipse at 50% 40%, transparent 40%, rgba(7,5,4,0.55) 100%)",
  },

  titleBlock: { textAlign: "center", zIndex: 2, marginBottom: 40, pointerEvents: "none" },
  kicker: { fontFamily: RM.fontD, fontSize: 13, letterSpacing: 5, color: RM.boneFaint },
  title: {
    fontFamily: RM.fontD,
    fontSize: 50,
    letterSpacing: 6,
    color: RM.bone,
    textShadow: `0 0 40px ${RM.vein}, 0 0 80px ${RM.fleshDeep}`,
    margin: "6px 0",
  },
  sub: { fontFamily: RM.font, fontStyle: "italic", fontSize: 16, color: RM.boneDim },

  menu: {
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    width: "min(360px, 86%)",
  },
  primary: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    padding: "15px 22px",
    background: RM.fleshDeep,
    border: `1px solid ${RM.fleshGlow}`,
    borderRadius: 11,
    cursor: "pointer",
    textAlign: "left",
    boxShadow: emberGlow(RM.vein),
  },
  ghost: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    padding: "15px 22px",
    background: "rgba(13,9,7,0.78)",
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 11,
    cursor: "pointer",
    textAlign: "left",
  },
  ghostSlim: {
    padding: "12px 22px",
    background: "rgba(13,9,7,0.62)",
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 11,
    cursor: "pointer",
    textAlign: "left",
  },
  btnLabel: { fontFamily: RM.fontD, fontSize: 19, letterSpacing: 2, color: RM.bone },
  btnSub: { fontFamily: RM.font, fontStyle: "italic", fontSize: 12.5, color: RM.boneDim },

  confirm: {
    zIndex: 2,
    width: "min(420px, 88%)",
    padding: "24px 26px",
    background: "rgba(13,9,7,0.95)",
    border: `1px solid ${RM.vein}`,
    borderRadius: 13,
    boxShadow: veinGlow(),
    textAlign: "center",
  },
  confirmTitle: { fontFamily: RM.fontD, fontSize: 21, color: RM.bone, marginBottom: 10 },
  confirmBody: {
    fontFamily: RM.font,
    fontSize: 15,
    color: RM.boneDim,
    lineHeight: 1.55,
    marginBottom: 20,
  },
  confirmRow: { display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" },
  danger: {
    padding: "12px 22px",
    background: RM.fleshDeep,
    border: `1px solid ${RM.fleshGlow}`,
    borderRadius: 10,
    color: RM.bone,
    fontFamily: RM.fontD,
    fontSize: 16,
    letterSpacing: 1,
    cursor: "pointer",
    boxShadow: emberGlow(RM.flesh),
  },

  settingsVeil: {
    position: "absolute",
    inset: 0,
    background: "rgba(7,5,4,0.74)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
  },
  settingsCard: {
    width: "min(400px, 88%)",
    padding: "24px 26px",
    background: RM.char2,
    border: `1px solid ${RM.vein}`,
    borderRadius: 14,
    boxShadow: veinGlow(),
  },
  settingsTitle: {
    fontFamily: RM.fontD,
    fontSize: 22,
    color: RM.bone,
    marginBottom: 16,
    borderBottom: `1px solid ${RM.charBorder}`,
    paddingBottom: 10,
  },
  pocketsEmpty: {
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 14,
    color: RM.boneFaint,
    padding: "8px 0 16px",
  },
  pocketsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 9,
    maxHeight: "52vh",
    overflowY: "auto",
    marginBottom: 8,
  },
  pocketItem: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    padding: "9px 12px",
    border: "1px solid",
    borderRadius: 9,
    background: "linear-gradient(180deg, #1a110c, #100a07)",
  },
  pocketSlot: {
    width: 36,
    height: 36,
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid",
    borderRadius: 7,
    background: "rgba(0,0,0,0.45)",
    fontFamily: RM.fontT,
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: 1,
  },
  pocketName: { fontFamily: RM.fontD, fontSize: 15, letterSpacing: 1, color: RM.bone },
  pocketDesc: {
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 12,
    color: RM.boneDim,
    lineHeight: 1.4,
    marginTop: 2,
  },
  toggleRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    padding: "12px 0",
    background: "transparent",
    border: "none",
    borderBottom: `1px solid ${RM.char3}`,
    cursor: "pointer",
    textAlign: "left",
  },
  toggleLabel: { fontFamily: RM.fontD, fontSize: 16, color: RM.bone },
  toggleHint: { fontFamily: RM.font, fontStyle: "italic", fontSize: 12, color: RM.boneFaint, marginTop: 2 },
  switch: {
    width: 46,
    height: 26,
    borderRadius: 13,
    background: RM.char3,
    border: `1px solid ${RM.charBorder}`,
    position: "relative",
    flexShrink: 0,
    transition: "background 160ms ease",
  },
  switchOn: { background: RM.moldDim, borderColor: RM.moldBright },
  knob: {
    position: "absolute",
    top: 2,
    left: 2,
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: RM.boneDim,
    transition: "all 160ms ease",
  },
  knobOn: { left: 22, background: RM.moldBright },
  closeBtn: {
    marginTop: 18,
    width: "100%",
    padding: "11px",
    background: RM.fleshDeep,
    border: `1px solid ${RM.fleshGlow}`,
    borderRadius: 10,
    color: RM.bone,
    fontFamily: RM.fontD,
    fontSize: 15,
    letterSpacing: 1,
    cursor: "pointer",
  },

  footer: {
    position: "absolute",
    bottom: 14,
    fontFamily: RM.fontD,
    fontSize: 11,
    letterSpacing: 2,
    color: RM.boneFaint,
    zIndex: 2,
  },
};
